import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  ArrowLeft,
  Building2,
  Calendar,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Users,
  Wallet,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";
import { env } from "@/lib/env";
import { formatMoneyMinor, computePlatformFee } from "@/lib/money";
import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/brand/PageLoader";
import { DownloadAppCTA } from "@/components/brand/DownloadAppCTA";

interface Court {
  id: string;
  name: string;
  format: string;
  base_price_minor: number | null;
  currency: string;
  description: string | null;
  venue: {
    venue_name: string;
    city: string;
    region: string;
    country: string;
    address_line1: string;
  } | null;
}

interface Slot {
  id: string;
  starts_at: string;
  ends_at: string;
  status: string;
  price_minor: number | null;
  match_id: string | null;
  match: {
    id: string;
    status: string;
    target_players: number;
    participants: { count: number }[] | null;
  } | null;
}

/**
 * Consumer court page. Shows week calendar; players click an open slot
 * to host a new match, or click an already-hosted-but-not-full slot to
 * join the existing match.
 */
export function CourtDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [court, setCourt] = useState<Court | null>(null);
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [slots, setSlots] = useState<Slot[] | null>(null);
  const [acting, setActing] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("courts")
        .select(`
          id, name, format, base_price_minor, currency, description,
          venue:venue_applications (
            venue_name, city, region, country, address_line1
          )
        `)
        .eq("id", id)
        .eq("published", true)
        .single();
      if (!cancelled) setCourt((data ?? null) as Court | null);
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      setSlots(null);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 7);
      // Two FKs link these tables (slots.match_id → matches.id and
      // matches.slot_id → slots.id); pin the embed to the forward
      // one so PostgREST doesn't error on ambiguity.
      const { data, error } = await supabase
        .from("availability_slots")
        .select(`
          id, starts_at, ends_at, status, price_minor, match_id,
          match:matches!availability_slots_match_id_fkey (
            id, status, target_players,
            participants:match_participants ( count )
          )
        `)
        .eq("court_id", id)
        .in("status", ["open", "booked"])
        .gte("starts_at", weekStart.toISOString())
        .lt("starts_at", weekEnd.toISOString())
        .order("starts_at");
      if (cancelled) return;
      if (error) {
        toast.error(error.message);
        setSlots([]);
        return;
      }
      setSlots((data ?? []) as Slot[]);
    })();
    return () => {
      cancelled = true;
    };
  }, [id, weekStart]);

  async function hostSlot(slotId: string) {
    if (!user) {
      navigate(`/login?next=/courts/${id}`);
      return;
    }
    setActing(slotId);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const jwt = sess?.session?.access_token;
      if (!jwt) throw new Error("Session expired");

      // Default target players handled server-side based on format.
      const res = await fetch(`${env.supabaseUrl}/functions/v1/match-create`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${jwt}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ slot_id: slotId, target_players: 0 }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error ?? `Server ${res.status}`);
      window.location.href = body.checkout_url as string;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Couldn't host slot");
    } finally {
      setActing(null);
    }
  }

  if (!court) return <PageLoader />;

  const unit = court.base_price_minor ?? 0;
  const feePreview = computePlatformFee(unit);
  const chargePreview = unit + feePreview;

  return (
    <section className="mx-auto max-w-5xl px-6 py-12">
      <Link
        to="/courts"
        className="mb-6 inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3 w-3" /> All courts
      </Link>

      <header className="mb-8">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-3xl font-semibold tracking-tight">{court.name}</h1>
          <span className="rounded-full bg-volt-subtle px-2 py-0.5 text-[10px] font-bold text-volt">
            {court.format}
          </span>
        </div>
        {court.venue && (
          <p className="mt-2 flex items-center gap-1 text-sm text-muted-foreground">
            <MapPin className="h-4 w-4" />
            {court.venue.venue_name}, {court.venue.address_line1},{" "}
            {court.venue.city}
          </p>
        )}
        {court.description && (
          <p className="mt-4 max-w-2xl text-sm text-muted-foreground">
            {court.description}
          </p>
        )}
        {unit > 0 && (
          <p className="mt-4 flex items-center gap-1 text-sm">
            <Wallet className="h-4 w-4 text-volt" />
            <span className="font-semibold">
              {formatMoneyMinor(chargePreview, court.currency)}
            </span>
            <span className="text-muted-foreground">
              per player ({formatMoneyMinor(unit, court.currency)} venue +{" "}
              {formatMoneyMinor(feePreview, court.currency)} Whistll fee)
            </span>
          </p>
        )}
      </header>

      {/* Player-facing download nudge. The web view exists for
          discovery + deep-link sharing; the actual host/join flow
          happens in the iOS / Android app. */}
      <DownloadAppCTA
        className="mb-8"
        headline="Want to play here?"
        subhead="Download Whistll to host or join a game at this court. Pay with a Stripe hold, get refunded automatically if it doesn't fill, all from your phone."
      />

      {/* Week nav */}
      <div className="mb-4 flex items-center gap-3">
        <button
          type="button"
          onClick={() => {
            const d = new Date(weekStart);
            d.setDate(d.getDate() - 7);
            setWeekStart(d);
          }}
          className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border hover:border-volt/60"
          aria-label="Previous week"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="inline-flex items-center gap-2 text-sm font-semibold">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          {formatWeek(weekStart)}
        </span>
        <button
          type="button"
          onClick={() => {
            const d = new Date(weekStart);
            d.setDate(d.getDate() + 7);
            setWeekStart(d);
          }}
          className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border hover:border-volt/60"
          aria-label="Next week"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {slots === null ? (
        <div className="flex h-40 items-center justify-center">
          <PageLoader />
        </div>
      ) : slots.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card/40 p-12 text-center">
          <Building2 className="mx-auto mb-3 h-6 w-6 text-muted-foreground" />
          <p className="text-sm font-semibold">No slots this week.</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Try a different week - or ask the venue to open more times.
          </p>
        </div>
      ) : (
        <ul className="grid gap-3">
          {slots.map((s) => {
            const active = s.match?.participants?.[0]?.count ?? 0;
            const target = s.match?.target_players ?? null;
            const joined =
              s.match_id && active >= 1 && s.match?.status !== "cancelled";
            return (
              <li
                key={s.id}
                className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <p className="font-semibold">{formatSlot(s.starts_at, s.ends_at)}</p>
                  <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                    {s.match ? (
                      <>
                        <Users className="h-3 w-3" />
                        {active} / {target} joined · match{" "}
                        {s.match.status}
                      </>
                    ) : (
                      "Open - be the first to host"
                    )}
                  </p>
                </div>
                {s.match_id && joined ? (
                  <Button asChild size="sm">
                    <Link to={`/matches/${s.match_id}`}>View match</Link>
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    disabled={acting === s.id || s.status !== "open"}
                    onClick={() => hostSlot(s.id)}
                  >
                    {acting === s.id ? "Opening Stripe…" : "Host this slot"}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

// -- Date helpers -------------------------------------------------------------

function mondayOf(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const day = x.getDay(); // 0=Sun
  const shift = day === 0 ? -6 : 1 - day;
  x.setDate(x.getDate() + shift);
  return x;
}

function formatWeek(weekStart: Date): string {
  const end = new Date(weekStart);
  end.setDate(end.getDate() + 6);
  const fmt = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" });
  return `${fmt.format(weekStart)} - ${fmt.format(end)}`;
}

function formatSlot(starts: string, ends: string): string {
  const s = new Date(starts);
  const e = new Date(ends);
  const day = new Intl.DateTimeFormat(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  }).format(s);
  const time = (d: Date) =>
    new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" }).format(d);
  return `${day} · ${time(s)} – ${time(e)}`;
}
